import { useMemo, useState } from 'react';

import { HorizontalMenuTabs, REMOTE_PERMISSIONS } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CoachingGuideButtons } from '@backrs_ui/grow-ui/src/components/CoachingGuideButtons/CoachingGuideButtons';
import {
  NoticeHost,
  useNotice,
} from '@backrs_ui/grow-ui/src/components/InstitutionWrite/NoticeHost';
import type { WriteOutcome } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/writeOutcome';
import { Header } from '@backrs_ui/grow-ui/src/components/Lubav/Header';
import { OperatorContextSwitcher } from '@backrs_ui/grow-ui/src/components/PageShell/OperatorContextSwitcher';
import { useHasPermission } from '@backrs_ui/grow-ui/src/hooks/useHasPermission';
import { useIsInstitutionAdmin } from '@backrs_ui/grow-ui/src/hooks/useIsInstitutionAdmin';
import { usePageTitle } from '@backrs_ui/grow-ui/src/hooks/usePageTitle';
import { useSandboxGate } from '@backrs_ui/grow-ui/src/hooks/useSandboxGate';
import {
  institutionLabel,
  cohortLabel,
  offeringLabel,
} from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import { OperatorRootPicker } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/OperatorRootPicker';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import type {
  AdminCode,
  AdminCohort,
  AdminRoster,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';
import {
  selectIsPlatformContext,
  selectOperatorInstitutionId,
} from '@backrs_ui/grow-ui/src/redux/operatorContext';

import { Box, Button, type SxProps, type Theme } from '@mui/material';

import { CohortCoachesPalette } from './CohortCoachesPalette';
import { CohortMembersPalette, type CohortMembersMode } from './CohortMembersPalette';
import { CohortsTab } from './CohortsTab';
import { CreateCodeDialog } from './CreateCodeDialog';
import { CreateCohortDialog } from './CreateCohortDialog';
import {
  coachesDialogStrings as coachesStrings,
  codeStrings,
  cohortStrings,
  renameStrings as renameCohortStrings,
} from './institutionAdminWriteMessages';
import { useAgentContext as useWritesAgentContext } from './institutionAdminWrites.agentContext';
import { OperatorsTab } from './OperatorsTab';
import { RenameCohortDialog } from './RenameCohortDialog';
import { RevokeCodeDialog } from './RevokeCodeDialog';
import { cohortLineKey, rosterLineKey } from './rosterCodes';
import { ALL, RostersAdminFilterBar } from './RostersAdminFilterBar';
import { useAgentContext } from './RostersAdminPageContainer.agentContext';
import {
  defaultRostersAdminTab,
  ROSTERS_ADMIN_TAB,
  ROSTERS_ADMIN_TAB_LABELS,
  rostersAdminTabsFor,
  WRITE_DOOR,
  type WriteDoor,
} from './rostersAdminTabs';
import { SeatSummary } from './SeatSummary';
import { useInstitutionAdminController } from './useInstitutionAdminController';
import { useOperatorsTabController } from './useOperatorsTabController';

const strings = appStrings.modules.rostersAdmin;

/** The line a create was opened from. */
type CreateCodeTarget = Readonly<{
  roster: AdminRoster;
  cohort: AdminCohort | null;
  lineKey: string;
}>;

/** The line a revoke was opened from, and the code that line shows. */
type RevokeTarget = Readonly<{
  roster: AdminRoster;
  cohort: AdminCohort | null;
  code: AdminCode;
  lineKey: string;
}>;

/**
 * A cohort door names its line by ID, not by the row that opened it. A held row is a copy
 * taken at open time, so a rename landing under an open dialog would leave the dialog
 * naming the cohort's old name; an ID sends both surfaces back to the one read.
 */
type CohortTarget = Readonly<{
  roster: AdminRoster;
  cohortId: number;
}>;

/** The rename door also carries the name it opened on, so its title names the cohort
 * rather than whatever has since been typed into the field. */
type RenameTarget = CohortTarget & Readonly<{ cohortName: string }>;

const rosterContextLabel = (roster: AdminRoster): string =>
  strings.rosterOptionLabel(
    offeringLabel(roster.offering),
    institutionLabel(roster.managingInstitution),
  );

const RostersAdminPage = () => {
  const isInstitutionAdmin = useIsInstitutionAdmin();
  const canAccessOperatorFleet = useHasPermission(REMOTE_PERMISSIONS.ACCESS_OPERATOR_FLEET);
  const { announce } = useNotice();

  const activeOperatorId = useAppSelector(selectOperatorInstitutionId);
  // Both halves: the stored literal alone would leave a caller whose grant was revoked on a
  // page with no tabs it can serve and no refusal to draw a picker from.
  const isPlatformMode = useAppSelector(selectIsPlatformContext) && canAccessOperatorFleet;
  const tabs = useMemo(
    () => rostersAdminTabsFor({ canAccessOperatorFleet, platformMode: isPlatformMode }),
    [canAccessOperatorFleet, isPlatformMode],
  );
  // The roster read resolves one operator, which platform mode does not name.
  const controller = useInstitutionAdminController({
    enabled: isInstitutionAdmin && !isPlatformMode,
    tabs,
  });
  const operators = useOperatorsTabController({
    enabled: isInstitutionAdmin && controller.tab === ROSTERS_ADMIN_TAB.OPERATORS,
  });

  // The roster the create dialog opens pointed at; null inside means the operator picks one.
  const [createCohortTarget, setCreateCohortTarget] = useState<{
    rosterId: number | null;
  } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);

  const gateInSandbox = useSandboxGate();
  /**
   * Every door onto a write goes through here. In the sandbox it opens the contact prompt and
   * the door stays shut, so a prospect sees what the action is without the page changing under
   * them; everywhere else it opens as normal.
   */
  const door =
    <Args extends unknown[]>(open: (...args: Args) => void) =>
    (...args: Args) => {
      if (gateInSandbox()) return;

      open(...args);
    };
  const [createCodeTarget, setCreateCodeTarget] = useState<CreateCodeTarget | null>(null);
  const [codeDraft, setCodeDraft] = useState('');
  const [coachesTarget, setCoachesTarget] = useState<CohortTarget | null>(null);
  const [membersTarget, setMembersTarget] = useState<
    (CohortTarget & Readonly<{ mode: CohortMembersMode }>) | null
  >(null);
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [cohortSearch, setCohortSearch] = useState('');

  usePageTitle(strings.title);
  useAgentContext({
    isInstitutionAdmin,
    tab: controller.tab,
    rosters: controller.rosters.rows,
    operators: operators.rows,
    activeOperatorId,
    home: operators.home,
    selectedInstitutionId: controller.selectedInstitutionId,
    cohortSearch,
  });
  const openDoor: WriteDoor | null =
    revokeTarget !== null
      ? WRITE_DOOR.REVOKE
      : createCodeTarget !== null
        ? WRITE_DOOR.MINT
        : coachesTarget !== null
          ? WRITE_DOOR.MANAGE_COACHES
          : membersTarget !== null
            ? WRITE_DOOR.MANAGE_MEMBERS
            : renameTarget !== null
              ? WRITE_DOOR.RENAME_COHORT
              : createCohortTarget !== null
                ? WRITE_DOOR.CREATE_COHORT
                : null;

  useWritesAgentContext({
    openDoor,
    rosterId:
      revokeTarget?.roster.id ??
      createCodeTarget?.roster.id ??
      coachesTarget?.roster.id ??
      membersTarget?.roster.id ??
      renameTarget?.roster.id ??
      createCohortTarget?.rosterId ??
      null,
    pinnedCohortId:
      revokeTarget?.cohort?.id ??
      createCodeTarget?.cohort?.id ??
      coachesTarget?.cohortId ??
      membersTarget?.cohortId ??
      renameTarget?.cohortId ??
      null,
  });

  const { heldRoots } = controller;

  const say = (outcome: WriteOutcome, fallback: string) =>
    announce(outcome.message ?? fallback, outcome.ok ? 'success' : 'error');

  const openCreateCode = (roster: AdminRoster, cohort: AdminCohort | null) => {
    controller.codeCreate.reset();
    setCodeDraft('');
    setCreateCodeTarget({
      roster,
      cohort,
      lineKey: cohort === null ? rosterLineKey(roster.id) : cohortLineKey(cohort.id),
    });
  };

  const createCode = async () => {
    if (createCodeTarget === null) return;

    const outcome = await controller.codeCreate.onCreate(
      createCodeTarget.roster.id,
      createCodeTarget.cohort?.id ?? null,
      createCodeTarget.lineKey,
      codeDraft,
    );

    if (outcome.ok) setCreateCodeTarget(null);

    say(outcome, codeStrings.mint.error);
  };

  const confirmRevoke = async () => {
    if (revokeTarget === null) return;

    const outcome = await controller.codeRevoke.onRevoke(
      revokeTarget.roster.id,
      revokeTarget.code.id,
      revokeTarget.lineKey,
    );

    // C2b: success closes. No open, re-armable post-success state exists.
    if (outcome.ok) setRevokeTarget(null);

    say(outcome, strings.revokeDialog.failed);
  };

  const renameCohort = async () => {
    if (renameTarget === null) return;

    const outcome = await controller.cohortRename.onSubmit(
      renameTarget.roster.id,
      renameTarget.cohortId,
      renameDraft,
    );

    // C2b: success closes. No open, re-armable post-success state exists.
    if (outcome.ok) setRenameTarget(null);

    say(outcome, renameCohortStrings.failed);
  };

  const openCreateCohort = (rosterId: number | null) => {
    controller.cohortCreate.reset();
    setCreateCohortTarget({ rosterId });
  };

  if (!isInstitutionAdmin) return null;

  return (
    <Box sx={containerSx}>
      <Header
        title={strings.title}
        stickerUrl={null}
        newChatLabel=""
        sx={headerSx}
        actionButtons={
          <>
            <CoachingGuideButtons idPrefix="rosters-admin" />
            {heldRoots === null && controller.tab !== ROSTERS_ADMIN_TAB.OPERATORS && (
              <Button
                id="rosters-admin-create-cohort"
                hierarchy="Primary"
                size="md"
                onClick={() => door(openCreateCohort)(null)}
              >
                {strings.createCohortDialog.openButton}
              </Button>
            )}
          </>
        }
      />
      <Box sx={contentSx}>
        <OperatorContextSwitcher id="rosters-admin-operator-context" />
        {heldRoots !== null ? (
          <OperatorRootPicker
            heldRoots={heldRoots}
            onSelect={controller.onOperatorInstitutionSelect}
          />
        ) : (
          <>
            {tabs.length > 1 ? (
              <HorizontalMenuTabs
                id="rosters-admin-tabs"
                variant="box"
                containerSxOverride={tabsSx}
                tabs={tabs.map((tab) => ROSTERS_ADMIN_TAB_LABELS[tab])}
                value={tabs.indexOf(controller.tab)}
                setValue={(index) =>
                  controller.onTabChange(tabs[index] ?? defaultRostersAdminTab(tabs))
                }
              />
            ) : null}
            {controller.tab === ROSTERS_ADMIN_TAB.OPERATORS ? (
              <OperatorsTab
                rows={operators.rows}
                total={operators.total}
                dropped={operators.dropped}
                isLoading={operators.isLoading}
                isError={operators.isError}
                errorMessage={operators.errorMessage}
                isSearching={operators.isSearching}
                draft={operators.draft}
                onDraftChange={operators.onDraftChange}
                sortDirection={operators.sortDirection}
                onToggleSort={operators.onToggleSort}
                page={operators.page}
                rowsPerPage={operators.rowsPerPage}
                onPageChange={operators.onPageChange}
                activeOperatorId={activeOperatorId}
                home={operators.home}
                isContextError={operators.isContextError}
                onOpenHome={operators.onOpenHome}
                onOpen={operators.onOpenOperator}
                onOpenMatchedInstitution={operators.onOpenMatchedInstitution}
              />
            ) : (
              <>
                <SeatSummary rosters={controller.rosters.rows} showContractLink />
                <RostersAdminFilterBar
                  institution={
                    controller.selectedInstitutionId === null
                      ? ALL
                      : String(controller.selectedInstitutionId)
                  }
                  institutionOptions={controller.institutions.map((institution) => ({
                    value: String(institution.id),
                    label: institutionLabel(institution),
                  }))}
                  onInstitutionChange={(value) =>
                    controller.onInstitutionChange(value === ALL ? null : Number(value))
                  }
                  roster={
                    controller.cohortRosterId === null ? ALL : String(controller.cohortRosterId)
                  }
                  rosterOptions={controller.rosters.rows.map((roster) => ({
                    value: String(roster.id),
                    // Roster display names come from offerings and repeat across contexts, so the
                    // managing institution is what tells two same-named options apart.
                    label: strings.rosterOptionLabel(
                      offeringLabel(roster.offering),
                      institutionLabel(roster.managingInstitution),
                    ),
                  }))}
                  onRosterChange={(value) =>
                    controller.onCohortRosterChange(value === ALL ? null : Number(value))
                  }
                  search={cohortSearch}
                  onSearchChange={setCohortSearch}
                />
                <CohortsTab
                  rosters={controller.rosters.rows.filter(
                    (roster) =>
                      controller.cohortRosterId === null || roster.id === controller.cohortRosterId,
                  )}
                  isRostersLoading={controller.rosters.isLoading}
                  isRostersError={controller.rosters.isError}
                  /**
                   * "A filter is set", not "a filter hid something": the tab mounts loaders only
                   * for in-scope rosters, so it never holds the un-narrowed assembly an exact
                   * answer would compare against. An organization with no cohorts at all reads as
                   * narrowed while any filter is set.
                   */
                  isNarrowed={
                    controller.cohortRosterId !== null ||
                    controller.selectedInstitutionId !== null ||
                    cohortSearch.trim() !== ''
                  }
                  search={cohortSearch}
                  doors={{
                    isCreatingFor: controller.codeCreate.isPendingFor,
                    onCreateCode: door(openCreateCode),
                    onManageCoaches: door((roster, cohort) =>
                      setCoachesTarget({ roster, cohortId: cohort.id }),
                    ),
                    onAddMembers: door((roster, cohort) =>
                      setMembersTarget({ roster, cohortId: cohort.id, mode: 'add' }),
                    ),
                    onRemoveMembers: door((roster, cohort) =>
                      setMembersTarget({ roster, cohortId: cohort.id, mode: 'members' }),
                    ),
                    onRename: door((roster, cohort) => {
                      controller.cohortRename.reset();
                      setRenameDraft(cohort.name ?? '');
                      setRenameTarget({
                        roster,
                        cohortId: cohort.id,
                        cohortName: cohortLabel(cohort),
                      });
                    }),
                    onRevoke: door((roster, cohort, code) => {
                      controller.codeRevoke.reset();
                      setRevokeTarget({
                        roster,
                        cohort,
                        code,
                        lineKey: cohortLineKey(cohort.id),
                      });
                    }),
                  }}
                />
              </>
            )}
          </>
        )}
      </Box>
      {createCohortTarget !== null ? (
        <CreateCohortDialog
          open
          onClose={() => setCreateCohortTarget(null)}
          rosters={controller.rosters.rows}
          rosterName={rosterContextLabel}
          initialRosterId={createCohortTarget.rosterId}
          isSubmitting={controller.cohortCreate.isPending}
          isError={controller.cohortCreate.isError}
          errorMessage={controller.cohortCreate.errorMessage}
          onSubmit={async ({ rosterId, draft }) => {
            const outcome = await controller.cohortCreate.onSubmit(rosterId, draft);

            say(outcome, cohortStrings.create.error);

            return outcome.ok;
          }}
          isWriteInFlight={controller.isWriteInFlight}
          isRostersUnavailable={controller.isRostersUnavailable}
        />
      ) : null}
      {coachesTarget !== null ? (
        <CohortCoachesPalette
          open
          onClose={() => setCoachesTarget(null)}
          rosterId={coachesTarget.roster.id}
          cohortId={coachesTarget.cohortId}
          institutionId={coachesTarget.roster.managingInstitution.id}
          doors={{
            isAttaching: controller.cohortCoaches.isAttaching,
            isDetachingFor: controller.cohortCoaches.isDetachingFor,
            isWriteInFlight: controller.isWriteInFlight,
            onAttach: async (coach, cohortName) => {
              const outcome = await controller.cohortCoaches.onAttach(
                coachesTarget.roster.id,
                coachesTarget.cohortId,
                cohortName,
                coach,
              );

              say(outcome, coachesStrings.attachFailed);

              return outcome;
            },
            onDetach: async (coach, cohortName) => {
              const outcome = await controller.cohortCoaches.onDetach(
                coachesTarget.roster.id,
                coachesTarget.cohortId,
                cohortName,
                coach,
              );

              say(outcome, coachesStrings.detachFailed);

              return outcome;
            },
          }}
        />
      ) : null}
      {membersTarget !== null ? (
        <CohortMembersPalette
          open
          onClose={() => setMembersTarget(null)}
          rosterId={membersTarget.roster.id}
          cohortId={membersTarget.cohortId}
          mode={membersTarget.mode}
        />
      ) : null}
      {renameTarget !== null ? (
        <RenameCohortDialog
          open
          onClose={() => setRenameTarget(null)}
          cohortName={renameTarget.cohortName}
          rosterName={rosterContextLabel(renameTarget.roster)}
          name={renameDraft}
          onNameChange={setRenameDraft}
          isSubmitting={controller.cohortRename.isSubmitting}
          isWriteInFlight={controller.isWriteInFlight}
          isError={controller.cohortRename.isError}
          errorMessage={controller.cohortRename.errorMessage}
          onSubmit={() => void renameCohort()}
        />
      ) : null}
      {createCodeTarget !== null ? (
        <CreateCodeDialog
          open
          onClose={() => setCreateCodeTarget(null)}
          subjectName={
            createCodeTarget.cohort === null
              ? offeringLabel(createCodeTarget.roster.offering)
              : cohortLabel(createCodeTarget.cohort)
          }
          rosterName={rosterContextLabel(createCodeTarget.roster)}
          code={codeDraft}
          onCodeChange={setCodeDraft}
          isSubmitting={controller.codeCreate.isPendingFor(createCodeTarget.lineKey)}
          isWriteInFlight={controller.isWriteInFlight}
          isError={controller.codeCreate.isErrorFor(createCodeTarget.lineKey)}
          errorMessage={controller.codeCreate.errorMessage}
          onSubmit={() => void createCode()}
        />
      ) : null}
      {revokeTarget !== null ? (
        <RevokeCodeDialog
          open
          onClose={() => setRevokeTarget(null)}
          subjectName={
            revokeTarget.cohort === null
              ? offeringLabel(revokeTarget.roster.offering)
              : cohortLabel(revokeTarget.cohort)
          }
          code={revokeTarget.code}
          isSubmitting={controller.codeRevoke.isPendingFor(revokeTarget.lineKey)}
          isWriteInFlight={controller.isWriteInFlight}
          isError={controller.codeRevoke.isErrorFor(revokeTarget.lineKey)}
          errorMessage={controller.codeRevoke.errorMessage}
          onConfirm={() => void confirmRevoke()}
        />
      ) : null}
    </Box>
  );
};

export const RostersAdminPageContainer = () => (
  <NoticeHost id="rosters-admin-notice">
    <RostersAdminPage />
  </NoticeHost>
);

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  paddingInline: backrs.spacing['spacing-3xl'],
});

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  gap: backrs.spacing['spacing-xl'],
  padding: backrs.spacing['spacing-3xl'],
});

const tabsSx: SxProps<Theme> = {
  alignSelf: 'flex-start',
};
